import { TrashIcon } from '@heroicons/react/24/outline';
import { useState, useCallback, useMemo } from 'react';
import { Button, Loading } from 'react-daisyui';
import classNames from 'classnames';
import Lightbox from 'yet-another-react-lightbox';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import Download from 'yet-another-react-lightbox/plugins/download';
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import Video from 'yet-another-react-lightbox/plugins/video';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import { randomString } from '../../../../../../../../functions/string.functions.ts';
import { MediaType } from '../../../../../../Chat.enum.ts';
import {
  formatDate,
  formatTime,
  parseSlides,
} from '../../../../../../Chat.functions.ts';
import { useChatContext } from '../../../../../../Chat.hook.ts';
import { tAttachment, tChatEvent } from '../../../../../../Chat.types.ts';
import useLeadsalesAIAction from '../../../../../MessageBar/components/NotesAction/LeadsalesAIAction.hook.ts';
import { isDocumentOrPdf } from '../../../../../MessageBar/components/Thumbnail/Thumbnail.functions.ts';
import AudioContent from '../MessageBubble/components/AudioContent/AudioContent.component.tsx';
import FileContent from '../MessageBubble/components/FileContent/FileContent.component.tsx';
import ImageContent from '../MessageBubble/components/ImageContent/ImageContent.component.tsx';
import LocationContent from '../MessageBubble/components/LocationContent/LocationContent.component.tsx';
import StickerContent from '../MessageBubble/components/StickerContent/StickerContent.component.tsx';
import VideoContent from '../MessageBubble/components/VideoContent/VideoContent.component.tsx';
import { isSummaryEvent } from '../../../../../../ConversationSummary/ConversationSummary.functions';
import SummaryContent from '../../../../../../ConversationSummary/components/SummaryContent.component';
import SummaryHeader from './components/SummaryHeader.component';
import './LeadsalesAIBubble.styles.scss';
import ChatFormattedText from 'src/components/Chat/ChatFormattedText.tsx';

export type TLeadsalesAIBubbleProps = {
  aiEvent: tChatEvent;
};

export default function LeadsalesAIBubble(props: TLeadsalesAIBubbleProps) {
  const { aiEvent } = props;
  const { selectedChat } = useChatContext();
  const { deleteEventLeadsalesAI, loading } = useLeadsalesAIAction();

  const [openLightbox, setOpenLightbox] = useState<boolean>(false);

  function onCloseLightbox() {
    setOpenLightbox(false);
  }

  function RenderFileType({ attachment }: { attachment: tAttachment }) {
    switch (attachment.mediaType) {
      case MediaType.IMAGE:
        return <ImageContent attachment={attachment} />;
      case MediaType.VIDEO:
        return <VideoContent attachment={attachment} />;
      case MediaType.AUDIO:
        return <AudioContent attachment={attachment} />;
      case MediaType.STICKER:
        return <StickerContent attachment={attachment} />;
      case MediaType.DOCUMENT:
        return <FileContent attachment={attachment} />;
      case MediaType.LOCATION:
        return (
          <LocationContent
            latitude={attachment.latitude ?? 0}
            longitude={attachment.longitude ?? 0}
          />
        );
      default:
        return <FileContent attachment={attachment} />;
    }
  }

  const handleDelete = useCallback(() => {
    deleteEventLeadsalesAI(aiEvent.id);
  }, [aiEvent.id, deleteEventLeadsalesAI]);

  const creatorName = useMemo(
    () =>
      selectedChat?.lead?.users?.find(
        (user) => user.auth_id === aiEvent.createdBy,
      )?.name,
    [selectedChat?.lead?.users, aiEvent.createdBy],
  );

  // Check if this is a conversation summary
  const isConversationSummary = isSummaryEvent(aiEvent);

  // Render conversation summary with special styling
  if (isConversationSummary) {
    const containerClasses = classNames(
      'bg-gradient-to-br from-blue-50 to-indigo-50',
      'border border-blue-200 border-l-4 border-l-blue-600',
      'rounded-lg p-4 mx-4 my-3',
      'shadow-sm hover:shadow-md transition-shadow duration-200',
      'max-w-4xl ml-auto',
    );

    return (
      <div
        className={containerClasses}
        role="article"
        aria-label="Resumen de conversación"
      >
        <SummaryHeader
          generatedAt={aiEvent.createdAt}
          onDelete={handleDelete}
          loading={loading}
        />

        <SummaryContent content={aiEvent.content?.text || ''} />
      </div>
    );
  }

  // Render regular LeadsalesAI bubble for non-summary events
  return (
    <div className="LeadsalesAIBubble">
      <div className="LeadsalesAIBubble__Header">
        <div className="LeadsalesAIBubble__Header__Container">
          <h1 className="LeadsalesAIBubble__Header__Container__Title">
            {aiEvent?.note?.title ?? 'Leadsales AI'}
          </h1>
        </div>
        <div className="LeadsalesAIBubble__Header__Actions">
          <Button size="xs" color="ghost" shape="circle" onClick={handleDelete}>
            {loading ? (
              <Loading className="LeadsalesAIBubble__Header__Actions__Icon" />
            ) : (
              <TrashIcon className="LeadsalesAIBubble__Header__Actions__Icon" />
            )}
          </Button>
        </div>
      </div>
      <ChatFormattedText text={aiEvent.content?.text || ''} />
      <div className="form-control gap-2">
        {aiEvent.content?.attachments?.map((attachment) => (
          <div
            key={randomString()}
            onClick={() => {
              if (
                attachment?.mimeType &&
                !isDocumentOrPdf(attachment?.mimeType)
              ) {
                setOpenLightbox(true);
              }
            }}
          >
            <RenderFileType attachment={attachment} />
          </div>
        ))}
        {openLightbox && (
          <Lightbox
            plugins={[Zoom, Captions, Thumbnails, Download, Fullscreen, Video]}
            open={openLightbox}
            close={onCloseLightbox}
            slides={parseSlides(aiEvent?.content?.attachments || [])}
          />
        )}
      </div>
      <div className="LeadsalesAIBubble__Bottom">
        <span className="LeadsalesAIBubble__Header__Container__Title">
          {creatorName}
        </span>
        | {formatDate(aiEvent?.createdAt ?? '')} |{' '}
        {formatTime(aiEvent?.createdAt ?? '')}
      </div>
    </div>
  );
}
